<template>
  <div class="timeline-round" :class="`round_${round.id}`">
    <div class="dashboard-box-date">
      {{ round.year }}
    </div>
    <div class="dashboard-box-timeline">
      <div class="row" v-show="editable">
        <div class="col-md-3">
          <p class="dashboard-label">Year</p>
        </div>
        <div class="col-md-9">
          <p class="dashboard-value">
            <multiple-choices-dropdown
              title="year"
              class="seamless overview"
              v-model="roundYear"
              :show-clear-all="false"
              :single-choice="true"
              :search-enabled="false"
              :choices="roundYears"
            />
          </p>
          <p class="text-danger" v-for="error in roundErrors.year" :key="error">
            {{ error }}
          </p>
        </div>
      </div>
      <div class="row" v-if="editable || round.type">
        <div class="col-md-3">
          <p class="dashboard-label">Type</p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ round.type | roundTypeLabel }}
          </p>
          <p v-show="editable" class="dashboard-value">
            <RoundTypeDropdown
              :value="round.type"
              @change="
                $store.commit('startupProfile/SET_PREVIOUS_ROUND_TYPE', {
                  round: round,
                  type: $event,
                })
              "
              class="seamless overview"
            />
          </p>
        </div>
      </div>
      <div class="row" v-if="editable || round.size">
        <div class="col-md-3">
          <p class="dashboard-label">
            <HelpTip
              title=""
              message="Size amount in €, eg. 100000"
              label="Size"
            />
          </p>
        </div>
        <div class="col-md-9">
          <p v-if="!editable" class="dashboard-value">
            {{ round.size }}
          </p>
          <input
            v-if="editable"
            class="form-control overview-input"
            placeholder="Size in EUR"
            type="number"
            v-model="roundSize"
          />
          <p class="text-danger" v-for="error in roundErrors.size" :key="error">
            {{ error }}
          </p>
        </div>
      </div>
      <div class="row" v-if="editable || round.securities.length">
        <div class="col-md-3">
          <p class="dashboard-label">Security</p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ round.securities | roundSecurityArrayLabels }}
          </p>
          <p v-show="editable" class="dashboard-value">
            <MultiCheckboxSelect
              class="is-fullwidth"
              :value="round.securities"
              @change="
                $store.commit('startupProfile/SET_PREVIOUS_ROUND_SECURITIES', {
                  round: round,
                  securities: $event,
                })
              "
              :choices="roundSecurityChoices"
            />
          </p>
        </div>
      </div>
      <div
        class="row"
        v-if="editable || (round.sources && round.sources.length)"
      >
        <div class="col-md-3">
          <p class="dashboard-label">Source</p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">
            {{ round.sources | previousRoundsSourceArrayLabels }}
          </p>
          <p v-show="editable" class="dashboard-value">
            <MultiCheckboxSelect
              class="is-fullwidth"
              :value="round.sources"
              @change="
                $store.commit('startupProfile/SET_PREVIOUS_ROUND_SOURCES', {
                  round: round,
                  sources: $event,
                })
              "
              :choices="previousRoundsSourceChoices"
            />
          </p>
        </div>
      </div>
      <div class="row" v-if="editable || round.investor">
        <div class="col-md-3">
          <p class="dashboard-label">Investors</p>
        </div>
        <div class="col-md-9">
          <p v-show="!editable" class="dashboard-value">{{ round.investor }}</p>
          <input
            v-show="editable"
            class="form-control overview-input"
            placeholder="Investors"
            :value="round.investor"
            @change="
              $store.commit('startupProfile/SET_PREVIOUS_ROUND_INVESTOR', {
                round: round,
                investor: $event.target.value,
              })
            "
          />
          <p
            class="text-danger"
            v-for="error in roundErrors.investor"
            :key="error"
          >
            {{ error }}
          </p>
        </div>
      </div>

      <div class="row" v-show="editable">
        <div class="col-sm-12">
          <button
            class="btn btn-danger btn-xs btn-transparent"
            @click="removeRound"
          >
            Remove round
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import MultiCheckboxSelect from 'components/MultiCheckboxSelect.vue'
import RoundTypeDropdown from './RoundTypeDropdown.vue'
import {
  roundTypeLabel,
  previousRoundsSourceArrayLabels,
  roundSecurityChoices,
  roundSecurityArrayLabels,
  previousRoundsSourceChoices,
} from 'services/investment-rounds'
import { numberToMoney, moneyToNumber } from '@/filters'
import { years } from '../../utils/dates'
import Store from 'store'
import HelpTip from '../../components/common/HelpTip'

export default {
  components: { MultiCheckboxSelect, RoundTypeDropdown, HelpTip },
  filters: {
    numberToMoney,
    roundTypeLabel,
    previousRoundsSourceArrayLabels,
    roundSecurityArrayLabels,
  },
  props: {
    round: {
      type: Object,
      default: () => {
        return {}
      },
    },
    idx: { type: Number, default: null },
    roundErrors: {
      type: Object,
      default() {
        return {}
      },
    },
    editable: { type: Boolean, default: () => false },
  },
  setup(props, context) {
    const startupFetchInProgress = computed(() => {
      return Store.state.startupProfile.fetchInProgress
    })

    const roundYear = computed({
      get() {
        return props.round.year
      },
      async set(value) {
        await Store.commit('startupProfile/SET_PREVIOUS_ROUND_YEAR', {
          round: props.round,
          year: value,
        })
        context.emit('yearChange', props.round, value)
        return value
      },
    })

    const roundSize = computed({
      get() {
        return props.round.size
      },
      async set(value) {
        await setRoundSize(value)
      },
    })

    const setRoundSize = (value) => {
      let roundValue = value
      const round = props.round
      if (round.size === roundValue) {
        return
      }
      if (!roundValue) {
        roundValue = null
      }
      Store.commit('startupProfile/SET_PREVIOUS_ROUND_SIZE', {
        round: round,
        size: roundValue,
      })
    }

    const roundYears = computed(() => {
      const yrs = years()
      return yrs
        .map((year) => {
          return {
            label: year,
            value: year,
          }
        })
        .reverse()
    })

    const removeRound = () => {
      Store.commit('startupProfile/REMOVE_PREVIOUS_ROUND', props.round.id)
    }

    return {
      roundSecurityChoices,
      previousRoundsSourceChoices,
      roundSize,
      setRoundSize,
      startupFetchInProgress,
      roundYears,
      roundYear,
      removeRound,
    }
  },
}
</script>
